import { skipToken, useQuery } from "@apollo/client/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { FieldDelta } from "@/components/@types";
import { usePendingSession } from "@/components/organisms/PendingSession/PendingSession.store";
import { Queries } from "@/constants";

/**
 * The pending change a tool call's record speaks for, and where it stands.
 *
 * ``pendingChangeIdentifier`` names that change: the record's own
 * ``pendingChange`` points at its *latest* change, which is the wrong one for
 * a card about an earlier operation. ``pendingChangeType`` is authoritative for
 * the type until that change has loaded, so a create never reads as an update.
 *
 * Shared by the card's header status (``ToolCallCardPendingStatus``) and the
 * record layout inside it (``ToolCallCardNodeLayout``). ``node`` is the record
 * from ``NODE_QUERY_WITH_PENDING_CHANGE``.
 */
export function usePendingChangeStatus({
  node,
  toolName,
  pendingChangeIdentifier,
  pendingChangeType,
}: {
  node: unknown;
  toolName?: string | null;
  pendingChangeIdentifier?: string | null;
  pendingChangeType?: string | null;
}) {
  // Query the specific PendingChange when an identifier is provided.
  const {
    data: pcData,
    loading: pcLoading,
    refetch: refetchPc,
  } = useQuery(
    Queries.PENDING_CHANGE_QUERY,
    // ``skipToken``, not ``skip``: a skipped query still answers a refetch by
    // name, and confirming or rejecting refetches ``PendingChangeQuery`` — so a
    // card with no change of its own asked the server for one named "".
    pendingChangeIdentifier
      ? {
          variables: { identifier: pendingChangeIdentifier },
          fetchPolicy: "cache-first",
        }
      : skipToken,
  );

  const nodePendingChange = useMemo(() => {
    const record = node as Record<string, unknown> | undefined;
    return (
      (record?.pendingChange as Record<string, unknown> | undefined) ?? null
    );
  }, [node]);

  // When the node's pendingChange FK changes (a new change superseded the
  // previous one), the backend snapshots resultingData on the old change.
  // Refetch the specific PendingChange to pick up the updated resultingData.
  const nodePcIdentifier = (nodePendingChange?.identifier as string) ?? null;
  const prevNodePcRef = useRef(nodePcIdentifier);
  useEffect(() => {
    if (!nodePcIdentifier || !pendingChangeIdentifier) return;
    if (nodePcIdentifier === prevNodePcRef.current) return;
    prevNodePcRef.current = nodePcIdentifier;
    if (nodePcIdentifier !== pendingChangeIdentifier) {
      refetchPc({ identifier: pendingChangeIdentifier });
    }
  }, [nodePcIdentifier, pendingChangeIdentifier, refetchPc]);

  // Prefer the specifically-queried PendingChange over the node's pendingChange
  const specificPendingChange = useMemo(() => {
    if (!pcData?.pendingChange) return null;
    return pcData.pendingChange as Record<string, unknown>;
  }, [pcData?.pendingChange]);

  const activePendingChange = specificPendingChange ?? nodePendingChange;

  // Use pendingChangeType (from the content's own pendingChange) as the
  // authoritative source. Falls back to the queried pending change's type,
  // then infers from toolName as last resort.
  const inferredChangeType = toolName?.split("_")[0] ?? null;
  const changeType =
    pendingChangeType ??
    (activePendingChange?.changeType as string | undefined) ??
    inferredChangeType;
  const changeStatus =
    (activePendingChange?.status as string | undefined) ?? null;
  const changeIdentifier =
    (activePendingChange?.identifier as string | undefined) ?? null;
  const isCreate = changeType === "create";
  const isConfirmed = changeStatus === "confirmed";
  const isRejected = changeStatus === "rejected";
  const isHistorical = isConfirmed || isRejected;

  // When the activePendingChange comes from the node's FK (which may be
  // a different, later change), check whether it actually matches this card.
  // Mismatched deltas should not be shown.
  const pendingChangeMatchesCard = (() => {
    if (specificPendingChange) return true;
    if (pendingChangeIdentifier && activePendingChange) {
      return (
        (activePendingChange.identifier as string) === pendingChangeIdentifier
      );
    }
    if (pendingChangeType && activePendingChange) {
      return (activePendingChange.changeType as string) === pendingChangeType;
    }
    return true;
  })();

  const nodeDeltas = useMemo(() => {
    if (!pendingChangeMatchesCard) return null;
    if (!activePendingChange?.deltas) return null;

    const graphqlDeltas = activePendingChange.deltas as Array<{
      fieldName: string;
      originalValue: unknown;
      currentValue: unknown;
      fieldType?: string | null;
    }>;

    // The field's kind too: a reference the record's fields do not cover is
    // drawn as one by it, not as text.
    return graphqlDeltas.map((d) => ({
      fieldName: d.fieldName,
      originalValue: d.originalValue,
      currentValue: d.currentValue,
      fieldType: d.fieldType,
    })) as FieldDelta[];
  }, [activePendingChange, pendingChangeMatchesCard]);

  // Something to show for the change: its outcome once decided, or confirm /
  // reject while the thread's pending session still holds it.
  const pendingSession = usePendingSession();
  const showsStatus =
    isHistorical ||
    Boolean(changeIdentifier && pendingSession?.pendingSessionId);
  // The card shows the change as a diff: a create, or an update with field
  // changes. The header status and the record's actions menu both key on it.
  const hasChanges = isCreate || (nodeDeltas?.length ?? 0) > 0;

  return {
    activePendingChange,
    changeType,
    changeStatus,
    changeIdentifier,
    isCreate,
    isConfirmed,
    isHistorical,
    nodeDeltas,
    pendingSession,
    showsStatus,
    hasChanges,
    /** The change this card names has not come back yet. */
    isLoadingChange: Boolean(pendingChangeIdentifier) && pcLoading && !pcData,
  };
}

/**
 * Stack a sticky bar under the ones above it in a thread.
 *
 * Each sticky layer - a tool call's header, the record title under it, the
 * record's tabs under that - sticks where the layer above it ends. Its height
 * changes as it renders, so the bar is measured, and ``variable`` is set on its
 * parent (which holds everything that sticks under it) to ``above`` plus that
 * height - or just that height, for the topmost bar, which has nothing above
 * it. ``gap`` adds space after the bar, for something drawn between it and
 * what sticks under it. Returns a ref callback for the bar: the bar is often
 * drawn only once its record has loaded, well after the component holding it
 * mounted. Once the bar is gone its parent is told nothing sticks there.
 */
export function useStickyBarBottom(variable: string, above?: string, gap = 0) {
  const detachRef = useRef<(() => void) | null>(null);
  return useCallback(
    (bar: HTMLDivElement | null) => {
      detachRef.current?.();
      detachRef.current = null;
      const holder = bar?.parentElement;
      if (!bar || !holder) return;
      const observer = new ResizeObserver(() => {
        // Unrounded: a bar a fraction taller than its rounded height would
        // cover the top of the one sticking under it.
        const bottom = gap + bar.getBoundingClientRect().height;
        holder.style.setProperty(
          variable,
          above ? `calc(var(${above}, 0px) + ${bottom}px)` : `${bottom}px`,
        );
      });
      observer.observe(bar);
      detachRef.current = () => {
        observer.disconnect();
        holder.style.removeProperty(variable);
      };
    },
    [variable, above, gap],
  );
}

const EMPTY_STUCK: ReadonlySet<string> = new Set();

/**
 * Whichever records of one card currently hold their title under its header.
 *
 * A card draws the top of the record stuck beneath it, and a card can be
 * showing several — the statements an import worked through, the records a
 * search presented. So each record says whether it is stuck and the card
 * counts them, rather than one record's answer speaking for all of them.
 */
const StuckRecordsContext = createContext<
  ((identifier: string, isStuck: boolean) => void) | null
>(null);

export const StuckRecordsProvider = StuckRecordsContext.Provider;

/** For a record: tell the card it is in whether its title is stuck. */
export function useReportStuck() {
  return useContext(StuckRecordsContext);
}

/** For a card: whether any record in it is stuck, and how they say so. */
export function useStuckRecords() {
  const [stuck, setStuck] = useState<ReadonlySet<string>>(EMPTY_STUCK);
  const report = useCallback((identifier: string, isStuck: boolean) => {
    setStuck((current) => {
      if (current.has(identifier) === isStuck) return current;
      const next = new Set(current);
      if (isStuck) {
        next.add(identifier);
      } else {
        next.delete(identifier);
      }
      return next;
    });
  }, []);
  return { anyStuck: stuck.size > 0, report };
}
